import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { InferDecisionMakers } from './api.graphql';
import type { InferDecisionMakersMutation, InferDecisionMakersMutationVariables } from './api.graphql';
import { eligibleDms } from './utils';

type Input = {
	title: string;
	description?: string;
	/**
	 * The inferred target's own location — `adminArea`/`countryCode` are real ISO codes per the
	 * scope/location inference prompt (e.g. `"IL"`, `"US"`), not full names. `stateCode` on the
	 * mutation's own input is renamed from `adminArea` here so callers don't need to know that.
	 */
	location: { city: string | null; adminArea: string | null; countryCode: string | null };
	confidenceThreshold?: number;
	maxTargets?: number;
	/** Not sent to petition_service -- only used by the middle-end to key its full-response log. */
	draftId?: string;
};

/** Full success payload (CHANGE-98274), returned uncapped so CHANGE-99000 can reuse it later. */
export type InferDecisionMakersResult = Extract<
	InferDecisionMakersMutation['inferDecisionMakers'],
	{ __typename: 'InferDecisionMakersSuccess' }
>;

/**
 * Thin wrapper over the middle-end `inferDecisionMakers` mutation, tracking its own success and
 * error the same way `inferPetitionScopeLocation`'s `api.ts` does. Returns `null` when there's no
 * title or no resolved `countryCode` to infer from (no call, no tracking) — the caller
 * (`useGenerateAiDraft`) is responsible for the FCM gate and the LOCAL-scope/US-target checks;
 * this is just the last-mile guard against a missing target. Throws on the typed-error arm and on
 * transport failure so the caller can discriminate success vs. failure; callers that treat this as
 * best-effort (the SAP background call) are responsible for swallowing the rejection.
 */
export async function inferDecisionMakers(
	input: Input,
	{
		utilityContext: {
			gql: { fetch },
		},
		track,
	}: { utilityContext: UtilityContext; track: TrackingFn },
): Promise<InferDecisionMakersResult | null> {
	if (!input.title || !input.location.countryCode) return null;

	let response: InferDecisionMakersMutation['inferDecisionMakers'];
	try {
		const { data } = await fetch<InferDecisionMakersMutation, InferDecisionMakersMutationVariables>({
			query: InferDecisionMakers,
			// Matches the mutation's @restrictPath directive; required for the call to resolve.
			path: '/infer-decision-makers',
			variables: {
				input: {
					title: input.title,
					description: input.description,
					confidenceThreshold: input.confidenceThreshold,
					maxTargets: input.maxTargets,
					draftId: input.draftId,
					relevantLocation: {
						countryCode: input.location.countryCode,
						city: input.location.city ?? undefined,
						stateCode: input.location.adminArea ?? undefined,
					},
				},
			},
			rejectOnError: true,
		});
		response = data.inferDecisionMakers;
	} catch (e: unknown) {
		void track('sap_dmi_error', { dmi_error_reason: 'TRANSPORT_ERROR' });
		throw e;
	}

	// `error` is the generated error-reason enum, tracked directly here (rather than round-tripped
	// through a thrown Error's message) so a new server value is a type error against
	// `dmi_error_reason`, not a silent collapse to TRANSPORT_ERROR.
	if (response.__typename !== 'InferDecisionMakersSuccess') {
		void track('sap_dmi_error', { dmi_error_reason: response.error });
		throw new Error(response.error);
	}

	void track('sap_dmi_completed', {
		dmi_status: response.status,
		dmi_confidence_threshold: response.effectiveParams.confidenceThreshold,
		dmi_confidence_threshold_source: response.effectiveParams.confidenceThresholdSource,
		dmi_max_targets: response.effectiveParams.maxTargets ?? undefined,
		dmi_max_targets_source: response.effectiveParams.maxTargetsSource,
		dmi_top_recommendations: response.topRecommendations.map(String),
		eligible_dms: eligibleDms(response),
	});

	return response;
}
